/**
 * Requests a fresh verification email (User Onboarding + Module Access --
 * Pilot). Mirrors ForgotPasswordPage's exact submit/anti-enumeration
 * pattern -- same generic success message regardless of whether the
 * account exists or is already verified.
 */
import { useState } from "react";
import { useForm } from "react-hook-form";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import { Alert, Box, Button, Link, Stack, TextField, Typography } from "@mui/material";
import authService from "@/services/authService";
import { extractErrorMessage } from "@/utils/errors";

interface ResendVerificationFormValues {
  email: string;
}

export function ResendVerificationPage() {
  const [searchParams] = useSearchParams();
  const [serverError, setServerError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ResendVerificationFormValues>({
    defaultValues: { email: searchParams.get("email") ?? "" },
  });

  const onSubmit = async (values: ResendVerificationFormValues) => {
    setServerError(null);
    setIsSubmitting(true);
    try {
      await authService.resendVerification(values.email);
      setSubmitted(true);
    } catch (error) {
      setServerError(extractErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <Stack spacing={2}>
        <Alert severity="success">
          If an account with that email exists and is not yet verified, a new verification link has
          been sent.
        </Alert>
        <Typography variant="body2" color="text.secondary" textAlign="center">
          Don&apos;t see it in your inbox? Please check your Spam/Junk folder too — verification
          emails sometimes land there.
        </Typography>
        <Link component={RouterLink} to="/login" variant="body2" textAlign="center">
          Back to sign in
        </Link>
      </Stack>
    );
  }

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
      <Stack spacing={2}>
        {serverError && <Alert severity="error">{serverError}</Alert>}
        <TextField
          label="Email"
          type="email"
          fullWidth
          error={Boolean(errors.email)}
          helperText={errors.email?.message}
          {...register("email", { required: "Email is required" })}
        />
        <Button type="submit" variant="contained" size="large" disabled={isSubmitting} fullWidth>
          {isSubmitting ? "Sending..." : "Resend verification email"}
        </Button>
        <Link component={RouterLink} to="/login" variant="body2" textAlign="center">
          Back to sign in
        </Link>
      </Stack>
    </Box>
  );
}
export default ResendVerificationPage;
